import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, KeyRound, Globe, Building, Loader, CheckCircle, XCircle } from 'lucide-react';

const InputField = ({ icon, label, ...props }) => (
    <div>
        <label htmlFor={props.id || props.name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
        <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">{icon}</span>
            <input
                {...props}
                className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
            />
        </div>
    </div>
);

const SelectField = ({ icon, label, options, ...props }) => (
    <div>
        <label htmlFor={props.id || props.name} className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
        <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">{icon}</span>
            <select
                {...props}
                className="w-full bg-gray-800/30 backdrop-blur-lg border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all appearance-none"
            >
                {options.map(opt => <option key={opt.value} value={opt.value} className="bg-gray-800 text-white">{opt.label}</option>)}
            </select>
        </div>
    </div>
);


const WithdrawInformation = () => {
    const { token, user } = useAuth();
    const [formData, setFormData] = useState({
        network: '',
        address: '',
        securityPin: '',
        exchange: '',
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'

    useEffect(() => {
        if (user) {
            setFormData({
                network: user.withdrawNetwork || '',
                address: user.withdrawAddress || '',
                securityPin: '', // Do not pre-fill security pin
                exchange: user.withdrawExchange || '',
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setMessage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            const res = await fetch('/api/profile/withdraw-info', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (res.ok) {
                setMessage('Withdrawal information saved successfully!');
                setMessageType('success');
            } else {
                throw new Error(data.msg || 'Failed to save information.');
            }
        } catch (err) {
            setMessage(err.message);
            setMessageType('error');
        } finally {
            setLoading(false);
            setFormData(prev => ({ ...prev, securityPin: '' })); // Clear pin field after submission
        }
    };

    const networkOptions = [
        { value: '', label: 'Select Network' },
        { value: 'TRC20', label: 'TRC20' },
        { value: 'Ethereum (ERC20)', label: 'Ethereum (ERC20)' },
        { value: 'SOL', label: 'SOL' },
        { value: 'BSC (BEP20)', label: 'BSC (BEP20)' },
        { value: 'TON', label: 'TON' },
    ];

    return (
        <>
        <Helmet>
            <title>Withdrawal Information | RatingApp</title>
            <meta name="description" content="Set up and manage your USDT withdrawal details and wallet address on RatingApp." />
        </Helmet>
        <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans p-4">
            <div className="max-w-2xl mx-auto">
                <motion.h1
                    className="text-4xl font-extrabold tracking-tighter text-center mb-2 bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    Withdrawal Settings
                </motion.h1>
                <p className="text-slate-400 text-center mb-8">Secure your funds by setting up your withdrawal information.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <SelectField
                        icon={<Globe size={20} />}
                        label="Select Network"
                        name="network"
                        value={formData.network}
                        onChange={handleChange}
                        options={networkOptions}
                        required
                    />

                    <InputField
                        icon={<ShieldCheck size={20} />}
                        label="Withdraw Address"
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Your wallet address"
                        required
                    />

                    <InputField
                        icon={<KeyRound size={20} />}
                        label="Set Security Pin"
                        type="password"
                        name="securityPin"
                        value={formData.securityPin}
                        onChange={handleChange}
                        placeholder="Enter a 4-6 digit PIN"
                        required
                        minLength="4"
                        maxLength="6"
                    />

                    <InputField
                        icon={<Building size={20} />}
                        label="Exchange (Optional)"
                        type="text"
                        name="exchange"
                        value={formData.exchange}
                        onChange={handleChange}
                        placeholder="e.g., Binance, Coinbase"
                    />

                    <motion.button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-4 rounded-2xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                        whileHover={{ scale: 1.02, boxShadow: '0 20px 30px -10px rgba(0, 255, 255, 0.25)' }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {loading ? <><Loader size={20} className="animate-spin" /> Saving...</> : 'Save Information'}
                    </motion.button>
                </form>
                
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className={`mt-4 text-center p-3 rounded-lg flex items-center justify-center gap-2 ${messageType === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
                        >
                            {messageType === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
                            <span>{message}</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
        </>
    );
};

export default WithdrawInformation;
